/**
 * Workforce composition — people in post at the selected date, by category.
 *
 * On the employee basis ICA and Casual are rendered FADED but present. Hiding
 * them would make the employee figure look like the whole workforce; fading
 * them shows exactly what the basis leaves out, which is the point of having a
 * basis toggle at all.
 */
import React from 'react';
import { BarRow } from './BarRow';
import { PanelShell, PanelSkeleton, PanelUnavailable } from './PanelShell';
import {
  CATEGORY_COLORS,
  CATEGORY_ORDER,
  SESSIONAL_CATEGORIES,
  categoryLabel,
  fmtNum,
  fmtPct,
  type DefLookup,
  type WorkforceBasis,
  type WorkforceSummary,
} from './apiContract';

interface CompositionPanelProps {
  summary: WorkforceSummary | null;
  basis: WorkforceBasis;
  periodLabel: string;
  loading: boolean;
  def: DefLookup;
}

const NOTE: Record<WorkforceBasis, string> = {
  employees:
    'Sessional categories shown faded — excluded from the employee basis, reported separately.',
  engaged:
    'All engaged categories counted. Each person is classed by the most permanent seat they hold, so nobody is counted twice.',
  positions:
    'People across the filled seats; the positions basis counts seats, which are shown in the tiles above.',
};

export const CompositionPanel: React.FC<CompositionPanelProps> = ({
  summary,
  basis,
  periodLabel,
  loading,
  def,
}) => {
  const people = summary?.people ?? {};
  const keys = [
    ...CATEGORY_ORDER.filter((k) => k in people),
    ...Object.keys(people).filter((k) => !CATEGORY_ORDER.includes(k)),
  ];
  const visible = keys.filter((k) => (people[k] ?? 0) > 0 || CATEGORY_ORDER.slice(0, 4).includes(k));
  const max = Math.max(0, ...visible.map((k) => people[k] ?? 0));
  const total = visible.reduce((s, k) => s + (people[k] ?? 0), 0);

  return (
    <PanelShell
      title="Workforce composition"
      defKey="composition"
      def={def}
      window={`People in post at ${periodLabel}, classed by the most permanent seat held. Follows the period picker.`}
      footnote={NOTE[basis]}
    >
      {loading && !summary ? (
        <PanelSkeleton height={150} />
      ) : !summary ? (
        <PanelUnavailable what="The composition mix" />
      ) : (
        <div>
          {visible.map((k) => {
            const faded = basis === 'employees' && SESSIONAL_CATEGORIES.includes(k);
            const value = people[k] ?? 0;
            return (
              <BarRow
                key={k}
                label={categoryLabel(k)}
                value={value}
                max={max}
                color={CATEGORY_COLORS[k] ?? '#B9C4D6'}
                faded={faded}
                valueText={
                  <span title={total ? `${fmtPct((value / total) * 100)} of people in post` : undefined}>
                    {fmtNum(value)}
                  </span>
                }
              />
            );
          })}
        </div>
      )}
    </PanelShell>
  );
};
